import { useTranslation } from "@shared/utils/internationalization";
import { Trans } from "@shared/utils/internationalization";
import { usePublicExchangeManager } from "@app-contexts/public/features/exchanges/hooks/usePublicExchangeManager";
import ExchangeCreditBanner from "@shared/modules/exchanges/components/ExchangeCreditBanner/ExchangeCreditBanner.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Link from "@ui-kit/navigation/Link/Link.web";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import HowCreditsWorkModal from "@shared/modules/exchanges/components/HowCreditsWorkModal";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import { getExceptionToastDescription } from "@shared/utils/errors/error-utils";
import { useCallback } from "react";
import { NotifierFactory } from "@shared/services/api/abstracts/notifiers/NotifierFactory";

const notifier = NotifierFactory.createNotifier();

/**
 * NavBarExchangeBanner Component
 *
 * Displays an exchange credit banner in the navbar for public pages.
 * This banner appears when a user has active exchange credit available.
 *
 * Features:
 * - Shows credit amount with currency formatting
 * - Links to venue organizer page
 * - Shows membership ticket exchange message when applicable
 * - "How credits work" button (opens modal)
 * - Cancel exchange button
 *
 * @example
 * ```tsx
 * <NavBarExchangeBanner />
 * ```
 */
export default function NavBarExchangeBanner() {
  const { t } = useTranslation("exchanges");

  const {
    formattedAmount,
    isExchangeActive,
    isMembershipTicketExchange,
    isItemizedExchange,
    totalTickets,
    venueData,
    cancelExchange,
    isCancellingExchange,
  } = usePublicExchangeManager();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleCancelExchange = useCallback(async () => {
    try {
      await cancelExchange();
      notifier.success(t("Exchange cancelled", { ns: "exchanges" }));
    } catch (error) {
      notifier.error(getExceptionToastDescription(error));
    }
  }, [cancelExchange]);

  // Early return if no banner to show
  if (!isExchangeActive) return null;

  return (
    <>
      <ExchangeCreditBanner.Public
        description={
          <Flex direction="row" alignItems="center" gap={2}>
            <Text size="md" fontWeight="bold" color="blue.800">
              <Flex direction="row" alignItems="center" gap={1}>
                <Text>
                  <Trans
                    i18nKey="You have a temporary exchange credit of {{amount}} available for events organized by <venueLink>{{venueName}}</venueLink>."
                    values={{
                      amount: formattedAmount,
                      venueName: venueData?.name,
                    }}
                    components={{
                      venueLink: (
                        <Link
                          display="inline-block"
                          href={`/o/${venueData?.slug}`}
                          colorPalette="info"
                          variant="underline"
                          fontWeight="bold"
                        />
                      ),
                    }}
                    ns="exchanges"
                  />
                </Text>
              </Flex>
              {isMembershipTicketExchange && (
                <>
                  {t("Please select {{quantity}} tickets to purchase.", {
                    quantity: totalTickets,
                  })}
                </>
              )}
            </Text>
          </Flex>
        }
        ctaLabel={t("How credits work")}
        onCta={onOpen}
        onCancel={handleCancelExchange}
        allowCancel={isItemizedExchange}
        isDisabled={isCancellingExchange}
      />
      <HowCreditsWorkModal isOpen={isOpen} onClose={onClose} />
    </>
  );
}
